import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { EntityCardProps } from '../../components/organisms/EntityCard'
import { convertContractsToEntity } from '../../domain/converters/Contracts'
import { getContracts } from '../../domain/usecases/Contracts'
import { filtersFactory } from '../../main/factories/filters.factory'
import { FilterItem } from '../../components/organisms/EntityFilter'
import { getEntitiesCount } from '../../domain/usecases/Reports'
import { usePermissions } from '../../hooks/usePermissions'
import { CancelContract } from '../../data/usecases/Contracts/RemoteCancelContract'
import { CancelContractModal } from '../../components/molecules/CancelContractModal'
import { Contract } from '../../domain/models/ContractsModel'
import { IndividualPersonModel } from '../../domain/models/IndividualPersonModel'
import { JuridicPersonModel } from '../../domain/models/JuridicPersonModel'

export type ContractListContextProviderProps = {
  children: ReactNode
  remoteGetContracts: getContracts
  remoteGetEntitiesCount: getEntitiesCount
  remoteCancelContract: CancelContract
}

export type ProjectOption = { id: string; name: string }

export type ContractListContextProviderValues = {
  contracts: EntityCardProps[]
  errorMessage: string
  loading: boolean
  filters: FilterItem[]
  projectOptions: ProjectOption[]
  projectFilter: string
  clientFilter: string
  unitFilter: string
  setProjectFilter: (v: string) => void
  setClientFilter: (v: string) => void
  setUnitFilter: (v: string) => void
}

export const ContractListContext = createContext(
  {} as ContractListContextProviderValues
)

export const ContractListContextProvider = ({
  children,
  remoteGetContracts,
  remoteGetEntitiesCount,
  remoteCancelContract
}: ContractListContextProviderProps) => {
  const [loading, setIsLoading] = useState(false)
  const [rawContracts, setRawContracts] = useState<Contract[]>([])
  const [errorMessage, setErrorMessage] = useState('')
  const [filters, setFilters] = useState<FilterItem[]>([])
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [contractToCancel, setContractToCancel] = useState<string | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)

  const [projectFilter, setProjectFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [unitFilter, setUnitFilter] = useState('')

  const history = useHistory()

  const { addToast } = useToasts()
  const { can } = usePermissions()

  useEffect(() => {
    if (errorMessage) {
      addToast(errorMessage, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }, [errorMessage])

  useEffect(() => {
    requestContracts()
  }, [])

  const requestContracts = async () => {
    setIsLoading(true)
    try {
      const contracts = await remoteGetContracts.getAll()
      setRawContracts(contracts)
    } catch (error) {
      setErrorMessage('Não foi possível carregar os Contratos.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRedirectToContractDetails = (id: string) => {
    history.push(`/contracts/${id}`, { id })
  }

  const handleCancelClick = (id: string) => {
    setContractToCancel(id)
    setShowCancelModal(true)
  }

  const handleCancelConfirm = async () => {
    if (!contractToCancel) return

    setCancelLoading(true)
    try {
      await remoteCancelContract.cancel(contractToCancel)
      addToast('Contrato cancelado com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })
      setShowCancelModal(false)
      setContractToCancel(null)
      await requestContracts()
    } catch (error) {
      addToast('Não foi possível cancelar o contrato.', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setCancelLoading(false)
    }
  }

  const handleCancelModalClose = () => {
    setShowCancelModal(false)
    setContractToCancel(null)
  }

  React.useEffect(() => {
    remoteGetEntitiesCount
      .getEntitiesCount()
      .then((entitiesCount) =>
        setFilters(filtersFactory(entitiesCount, history))
      )
  }, [])

  const projectOptions = useMemo<ProjectOption[]>(() => {
    const map = new Map<string, string>()
    rawContracts.forEach((c) => {
      if (c.project?.id && c.project?.name) {
        map.set(c.project.id, c.project.name)
      }
    })
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    )
  }, [rawContracts])

  const filteredRaw = useMemo(() => {
    const client = clientFilter.trim().toLowerCase()
    const unit = unitFilter.trim().toLowerCase()
    return rawContracts.filter((c) => {
      if (projectFilter && c.project?.id !== projectFilter) return false
      if (client) {
        const match = c.buyers?.some((b) => {
          const name =
            b.info_type === 'IndividualPerson'
              ? (b.info as IndividualPersonModel)?.name
              : (b.info as JuridicPersonModel)?.business_name
          return name?.toLowerCase().includes(client)
        })
        if (!match) return false
      }
      if (unit) {
        const u = c.unit?.name?.toLowerCase() || ''
        if (!u.includes(unit)) return false
      }
      return true
    })
  }, [rawContracts, projectFilter, clientFilter, unitFilter])

  const contracts = useMemo(
    () =>
      convertContractsToEntity(
        filteredRaw,
        can.read('contracts') ? handleRedirectToContractDetails : undefined,
        // backend exige contracts.delete em POST /contracts/:id/cancel
        can.delete('contracts') ? handleCancelClick : undefined
      ),
    [filteredRaw, can]
  )

  return (
    <ContractListContext.Provider
      value={{
        contracts,
        errorMessage,
        loading,
        filters,
        projectOptions,
        projectFilter,
        clientFilter,
        unitFilter,
        setProjectFilter,
        setClientFilter,
        setUnitFilter
      }}
    >
      {children}
      <CancelContractModal
        show={showCancelModal}
        onHide={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
        loading={cancelLoading}
      />
    </ContractListContext.Provider>
  )
}

export const useContractList = () => {
  return useContext(ContractListContext)
}
